/**
 * Button Component.
 */
import * as React from 'react';

import { merge } from 'lodash';
import { ViewStyle, TouchableOpacityProps, Platform } from 'react-native';

import { Loader } from 'components/base/loader';
import { Colors } from 'theme/colors';
import { ACTIVE_OPACITY } from 'utils/constants';
import { ROUNDED_CORNER } from 'theme/sizes';
import { NakedIcon } from 'theme/icons';
import { Fill } from 'theme/layout';
import { Title } from 'theme/text';
import { AnimationListType } from 'theme/animations';

import { IconContainer, Touchable } from './styles';

const ICON_SIZE = 13;
const LOADER_SIZE = 20;

/**
 * Standard themed button component.
 *
 * @param props - Render props
 */
export function Button(props: ButtonProps) {
  const { animation, children, disabled, icon, iconSize, loading, style, ...otherProps } = props;

  const customStyle = getStyle(props);
  const content = Array.isArray(children)
    ? children.map((child, index) => getContent(child, props, String(index)))
    : getContent(children, props);

  return (
    <Touchable
      style={merge(style, customStyle)}
      disabled={disabled || loading}
      activeOpacity={ACTIVE_OPACITY}
      {...otherProps}>
      {loading ? <ButtonLoader animation={animation} /> : content}
      {icon && (
        <IconContainer>
          <NakedIcon name={icon} size={iconSize || ICON_SIZE} color={Colors.white} />
        </IconContainer>
      )}
    </Touchable>
  );
}
Button.defaultProps = {
  color: Colors.white,
};

/**
 * Determines the style properties of button from a set of properties.
 *
 * @param props - Render props
 */
function getStyle(props: ButtonProps) {
  const { accentColor = Colors.green, disabled, loading, rounded } = props;

  return {
    backgroundColor: loading ? accentColor : disabled ? Colors.mediumGrey : accentColor,
    borderRadius: rounded ? ROUNDED_CORNER : undefined,
  };
}
/**
 * Loader component used to indicate an action is in progress.
 *
 * @param props - Button loader props
 */
function ButtonLoader(props: ButtonLoaderProps) {
  const { animation } = props;
  const animationType = animation || 'greenPulse';

  if (Platform.OS === 'web') {
    return <Loader animationType={animationType} size={LOADER_SIZE} />;
  }

  return (
    <Fill justifyContent="center" alignItems="center">
      <Loader animationType={animationType} size={LOADER_SIZE} />
    </Fill>
  );
}
/**
 * Renders content for the button on the following use cases:
 * * A component is given as a child
 * * Any form of text will be wrapped.
 *
 * @param content - Content to render inside the button.
 * @param props - Color to apply top the text.
 * @param key - Key to apply to component if required in an array.
 */
function getContent(content: React.ReactNode, props: Pick<ButtonProps, 'color'>, key?: string) {
  return content instanceof React.Component ? (
    content
  ) : (
    // eslint-disable-next-line react/prop-types
    <Title key={key} color={props.color}>
      {content}
    </Title>
  );
}
/* Type definitions */
export type ButtonProps = TouchableOpacityProps &
  ViewStyle &
  App.Theme.FontProps & {
    readonly accentColor?: string;
    readonly animation?: AnimationListType;
    readonly icon?: string;
    readonly iconSize?: number;
    readonly loading?: boolean;
    readonly rounded?: boolean;
  };

interface ButtonLoaderProps {
  /** Type of animation to show when loading  */
  animation?: AnimationListType;
}
